import React, { useEffect, useState } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { Talkr } from 'talkr';
import { HashRouter, Routes, Route } from 'react-router-dom';
import defaultLang from './en.json';
import ViewportSizes from './components/viewport';
import firebaseConfig from './firebaseConfig';
import replaceUrl from './utils/scripts/replaceUrl';
import { initOneTrustTracking } from './utils/scripts/initOneTrustTracking';
import { initTikTokPixel } from './analytics/initTikTokPixel';
import { initGoogleAds } from './analytics/initGoogleAds';
import App from './App';
import { useAppStore } from './store';
import getParameterFromURL from './utils/scripts/getParameterFromURL';
import getStartPageTheme from './utils/scripts/getStartPageTheme';
import { setupPrerenderHydration } from './utils/scripts/prerenderShell';
import * as configs from './survey/configs';
import { startScreens } from './survey/startScreens';

import './index.scss';

window.dataLayer = window.dataLayer || [];
window.defaultLocales = defaultLang;
// Ранний preload первого экрана (LCP). Пока качается/исполняется main.js,
// параллельно прогреваем lazy-чанк стартовой страницы и тянем hero-картинку.
// Конфиг → первый шаг резолвится так же, как в useResolvedConfig.
{
  const startCfg = getParameterFromURL('config');
  const startMap = configs as unknown as Record<string, Array<{ name: string }>>;
  const startResolved = startCfg && startCfg in configs ? startMap[startCfg] : startMap.customV18;
  const startScreen = startResolved?.[0]?.name ? startScreens[startResolved[0].name] : undefined;

  if (startScreen) {
    startScreen.load();

    const isDark = getParameterFromURL('darkTheme') === 'true';
    // На широких вьюпортах dImg-экраны рендерят bgBig → тянем heroDesktop.
    const isWide = window.innerWidth >= 768;
    // Тематический первый экран (heroByTheme) перекрывает dark/desktop: у вариантов
    // один ассет на все вьюпорты и обе темы.
    const themedHero = startScreen.heroByTheme && startScreen.heroByTheme[getStartPageTheme()];
    const hero = themedHero
      || (isDark && startScreen.heroDark)
      || (isWide && startScreen.heroDesktop)
      || startScreen.hero;

    // Prerendered-страницы уже несут статический <link rel=preload> с media —
    // не дублируем его (см. prerenderFirstScreen.js).
    if (hero && !document.querySelector(`link[rel="preload"][as="image"][href="${hero}"]`)) {
      const heroLink = document.createElement('link');
      heroLink.rel = 'preload';
      heroLink.as = 'image';
      heroLink.type = 'image/avif';
      heroLink.href = hero;
      heroLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(heroLink);
    }
  }
}

initTikTokPixel();
initGoogleAds();
firebaseConfig();
replaceUrl();
initOneTrustTracking();

let userLang = window.navigator.language.slice(0, 2);

// First-screen locale keys embedded by the pre-render (commands/prerenderFirstScreen.js).
// Lets the pre-rendered first screen re-localize instantly on hydration, before the
// full ./locales/<lang>.json fetch lands. Null on a normal SPA page.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function readPreloadedLocale(lang: string): Record<string, any> | null {
  try {
    const el = document.getElementById('__PRELOADED_I18N__');
    if (!el || !el.textContent) return null;
    const map = JSON.parse(el.textContent);
    return map[lang] || null;
  } catch (e) {
    return null;
  }
}

type LocalisationsProps = { children: React.ReactNode };

function Localisations({ children }: LocalisationsProps) {
  const forceLang = useAppStore((state) => state.forceLang);
  const allowedLanguages = ['en', 'ru', 'es', 'fr', 'de', 'pt', 'it', 'tr', 'pl', 'uk'];
  const selectedLang = allowedLanguages.includes(userLang) ? userLang : 'en';

  // Seed the initial locale SYNCHRONOUSLY from the embedded first-screen keys, so the
  // FIRST (hydration) render is already in the browser language — matching the
  // pre-paint-swapped static DOM (see buildI18nSwapScript). Same lang the inline swap
  // picked → no hydration mismatch, no en→lang flash. The full ./locales fetch below
  // then replaces it. Merged over defaultLang so non-first-screen keys keep en.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [userLocale, setUserLocale] = useState<any>(() => {
    const preloaded = selectedLang !== 'en' ? readPreloadedLocale(selectedLang) : null;
    return preloaded ? { ...defaultLang, ...preloaded } : defaultLang;
  });

  useEffect(() => {
    const lang = forceLang || selectedLang;
    const wasSeeded = selectedLang !== 'en' && !!readPreloadedLocale(selectedLang);
    window.userLang = selectedLang;

    fetch(`./locales/${lang}.json`)
      .then((response) => response.json())
      .then((data) => {
        setUserLocale(data);
        window.locales = data;
        window.userLang = selectedLang;
        const htmlEl = document.querySelector('html');
        if (htmlEl) htmlEl.lang = selectedLang;
      })
      .catch(() => {
        // Keep the embedded first-screen locale if we seeded one; otherwise fall to en.
        if (wasSeeded) return;
        userLang = 'en';
        window.userLang = 'en';
        window.locales = defaultLang;
        setUserLocale(defaultLang);
      });
  }, []);

  return (
    <Talkr
      languages={{ en: defaultLang, [userLang]: userLocale }}
      defaultLanguage="en"
      detectBrowserLanguage
    >
      {children}
    </Talkr>
  );
}

const rootElement = document.getElementById('root')!;

// One tree for both boot paths — hydration must render EXACTLY what the captured
// markup was rendered with.
const appTree = (
  <HashRouter>
    <React.StrictMode>
      <Localisations>
        <Routes>
          <Route path="/" element={<App />} />
        </Routes>
        <ViewportSizes />
      </Localisations>
    </React.StrictMode>
  </HashRouter>
);

const mountApp = (): void => {
  createRoot(rootElement).render(appTree);
};

const hydrateApp = (): void => {
  hydrateRoot(rootElement, appTree, {
    // Surfaces hydration mismatches (with the component stack) that production
    // React otherwise reports only as minified error codes.
    onRecoverableError: (error, errorInfo) => {
      // eslint-disable-next-line no-console
      console.warn('[hydration]', error, errorInfo?.componentStack);
    },
  });
};

// Pre-rendered first screen: adopt the static markup via hydrateRoot (stores are
// pre-set and the first-screen chunk resolved first) — see prerenderShell.ts.
// A normal index.html has no __INITIAL_CONFIG__, so we mount immediately as before.
if (!setupPrerenderHydration(rootElement, hydrateApp, mountApp)) {
  mountApp();
}
